/* eslint-disable jsx-a11y/alt-text */
import { Box, Grid, Typography } from '@material-ui/core';
import React from 'react';
import { simpleDateFormatter2 } from '../Shared/colorFormat';
import { parseHrFormNote } from '../common/hrFormNote';

const lineValue = (value: any) => {
  if (value === 0) return '0';
  if (!value) return '................................................';
  return `${value}`;
};

const firstNonEmpty = (...values: any[]) => {
  for (const value of values) {
    if (value === 0) return value;
    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
      return value;
    }
  }
  return '';
};

const labelValue = ({ label, value }: any) => {
  return (
    <Box display="flex" mb={1}>
      <Typography style={{ fontWeight: 'bold', minWidth: 190 }}>
        {label}
      </Typography>
      <Typography>{lineValue(value)}</Typography>
    </Box>
  );
};

const getEmployeeName = (employee: any, row: any, isRTL: boolean) => {
  if (isRTL) {
    return (
      employee?.nameAr ||
      employee?.name ||
      row?.employeeNameAr ||
      row?.employeeName ||
      ''
    );
  }
  return (
    employee?.name ||
    employee?.nameAr ||
    row?.employeeName ||
    row?.employeeNameAr ||
    ''
  );
};

const getMeta = (row: any) => {
  return parseHrFormNote(row?.note) || {};
};

export class StartWorkPrint extends React.PureComponent<any, any> {
  renderHeader = (company: any) => (
    <Grid item xs={12}>
      {company?.header ? (
        <img
          src={company?.header}
          alt={company?.name}
          height="auto"
          width="100%"
          style={{ objectFit: 'contain', marginBottom: 10 }}
        />
      ) : (
        <Typography
          style={{ textAlign: 'center', fontWeight: 'bold' }}
          variant="h5"
        >
          {company?.name || ''}
        </Typography>
      )}
    </Grid>
  );

  renderSignatures = (isRTL: boolean) => (
    <Box mt={5}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography>{isRTL ? 'اسم الموظف:' : 'Employee Name:'}</Typography>
          <Typography style={{ marginTop: 4 }}>
            ................................................
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>{isRTL ? 'اسم المسؤول:' : 'Manager Name:'}</Typography>
          <Typography style={{ marginTop: 4 }}>
            ................................................
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>
            {isRTL ? 'توقيع الموظف:' : 'Employee Signature:'}
          </Typography>
          <Typography style={{ marginTop: 4 }}>
            ................................................
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>
            {isRTL ? 'توقيع المسؤول:' : 'Manager Signature:'}
          </Typography>
          <Typography style={{ marginTop: 4 }}>
            ................................................
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );

  renderNewStartForm = ({ row, employee, isRTL, meta }: any) => {
    const extra = meta?.extra || {};
    const jobTitle = extra?.jobTitle || employee?.speciality;

    return (
      <>
        <Typography
          style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 16 }}
        >
          {isRTL
            ? 'نموذج مباشرة عمل موظف جديد'
            : 'Start Work Form - New Employee'}
        </Typography>
        <Typography style={{ marginBottom: 12 }}>
          {isRTL ? 'اليوم:' : 'Date:'}{' '}
          {simpleDateFormatter2(row?.time || new Date())}
        </Typography>

        {labelValue({
          label: isRTL ? 'اولا: بيانات الموظف' : 'Employee Information',
          value: '',
        })}
        {labelValue({
          label: isRTL ? 'الاسم' : 'Name',
          value: getEmployeeName(employee, row, isRTL),
        })}
        {labelValue({
          label: isRTL ? 'المسمى الوظيفي' : 'Job Title',
          value: jobTitle,
        })}
        {labelValue({
          label: isRTL ? 'الجنسية' : 'Nationality',
          value: extra?.nationality || employee?.national,
        })}
        {labelValue({
          label: isRTL ? 'الديانة' : 'Religion',
          value: extra?.religion,
        })}
        {labelValue({
          label: isRTL ? 'رقم الجوال' : 'Phone',
          value: employee?.phone,
        })}
        {labelValue({
          label: isRTL ? 'القسم' : 'Department',
          value:
            (isRTL ? employee?.departmentNameAr : employee?.departmentName) ||
            (isRTL ? row?.departmentNameAr : row?.departmentName),
        })}
        {labelValue({
          label: isRTL ? 'تاريخ المباشرة' : 'Start Date',
          value: simpleDateFormatter2(row?.time || new Date()),
        })}
        {labelValue({
          label: isRTL ? 'الراتب الاساسي' : 'Basic Salary',
          value: employee?.basicSalary,
        })}
        {labelValue({
          label: isRTL ? 'اجمالي الراتب' : 'Total Salary',
          value: employee?.fullSalary,
        })}
        {labelValue({
          label: isRTL ? 'مدة العقد' : 'Contract Period',
          value: extra?.contractPeriod,
        })}
        {labelValue({
          label: isRTL ? 'فترة التجربة' : 'Probation Period',
          value: extra?.probationPeriod,
        })}
        {labelValue({
          label: isRTL ? 'العنوان' : 'Address',
          value: extra?.address || employee?.address,
        })}
        {labelValue({
          label: isRTL ? 'اسم المشرف' : 'Supervisor Name',
          value: extra?.supervisorName,
        })}

        <Box mt={2}>
          <Typography style={{ lineHeight: 1.9 }}>
            {isRTL
              ? 'نقر بان الموظف المذكور أعلاه قد باشر العمل فعليا لدى الشركة اعتبارا من التاريخ المحدد، وتطبق عليه جميع الانظمة واللوائح الداخلية وسياسات الشركة.'
              : 'We confirm that the employee above has started work effectively from the specified date and is subject to all company policies and internal regulations.'}
          </Typography>
        </Box>

        <Box mt={2}>
          <Typography style={{ fontWeight: 'bold' }}>
            {isRTL ? 'ملاحظات:' : 'Notes:'}
          </Typography>
          <Typography>{lineValue(meta?.notes || row?.desc)}</Typography>
        </Box>

        {this.renderSignatures(isRTL)}

        <Box style={{ pageBreakBefore: 'always' }}>
          <Typography
            style={{
              textAlign: 'center',
              fontWeight: 'bold',
              marginBottom: 12,
            }}
          >
            {isRTL ? 'ملحق نموذج مباشرة العمل' : 'Start Work Form - Appendix'}
          </Typography>
          <Typography style={{ marginBottom: 8 }}>
            {isRTL ? 'عناصر التحقق:' : 'Verification Checklist:'}
          </Typography>
          <Typography>
            {isRTL
              ? '☐ تم تسليم الهوية الوظيفية'
              : '☐ Employee ID card delivered'}
          </Typography>
          <Typography>
            {isRTL ? '☐ تم تسليم عهدة العمل' : '☐ Work assets delivered'}
          </Typography>
          <Typography>
            {isRTL ? '☐ تم فتح الملف المالي' : '☐ Payroll profile activated'}
          </Typography>
          <Typography>
            {isRTL ? '☐ تم ابلاغ الادارات المعنية' : '☐ Departments notified'}
          </Typography>
          <Box mt={3}>
            <Typography>
              {isRTL ? 'اسم مسؤول الموارد البشرية:' : 'HR Officer Name:'}
            </Typography>
            <Typography>
              ................................................
            </Typography>
            <Typography style={{ marginTop: 8 }}>
              {isRTL ? 'التوقيع:' : 'Signature:'}
            </Typography>
            <Typography>
              ................................................
            </Typography>
          </Box>
        </Box>
      </>
    );
  };

  renderReturnFromVacationForm = ({ row, employee, isRTL, meta }: any) => {
    const extra = meta?.extra || {};
    const vacationStart = firstNonEmpty(
      extra?.vacationStart,
      row?.vacationStart,
      employee?.vacationStart,
    );
    const vacationEnd = firstNonEmpty(
      extra?.vacationEnd,
      row?.vacationEnd,
      employee?.vacationEnd,
    );

    return (
      <>
        <Typography
          style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 16 }}
        >
          {isRTL ? 'نموذج مباشرة عمل بعد اجازة' : 'Return From Vacation Form'}
        </Typography>
        <Typography style={{ marginBottom: 12 }}>
          {isRTL ? 'اليوم:' : 'Date:'}{' '}
          {simpleDateFormatter2(row?.time || new Date())}
        </Typography>

        {labelValue({
          label: isRTL ? 'الاسم' : 'Name',
          value: getEmployeeName(employee, row, isRTL),
        })}
        {labelValue({
          label: isRTL ? 'المسمى الوظيفي' : 'Job Title',
          value: extra?.jobTitle || employee?.speciality,
        })}
        {labelValue({
          label: isRTL ? 'تاريخ بداية الاجازة' : 'Vacation Start Date',
          value: simpleDateFormatter2(vacationStart),
        })}
        {labelValue({
          label: isRTL ? 'تاريخ نهاية الاجازة' : 'Vacation End Date',
          value: simpleDateFormatter2(vacationEnd),
        })}
        {labelValue({
          label: isRTL ? 'تاريخ المباشرة بعد الاجازة' : 'Return Date',
          value: simpleDateFormatter2(row?.time || new Date()),
        })}
        {labelValue({
          label: isRTL ? 'القسم' : 'Department',
          value:
            (isRTL ? employee?.departmentNameAr : employee?.departmentName) ||
            (isRTL ? row?.departmentNameAr : row?.departmentName),
        })}
        {labelValue({
          label: isRTL ? 'اسم المشرف' : 'Supervisor Name',
          value: extra?.supervisorName,
        })}

        <Box mt={2}>
          <Typography style={{ lineHeight: 1.9 }}>
            {isRTL
              ? 'اقر انا الموقع ادناه باني باشرت العمل بعد انتهاء الاجازة، والتزم بجميع الانظمة والتعليمات المعمول بها في الشركة.'
              : 'I confirm that I resumed duty after vacation and commit to all policies and regulations of the company.'}
          </Typography>
        </Box>

        <Box mt={2}>
          <Typography style={{ fontWeight: 'bold' }}>
            {isRTL ? 'ملاحظات:' : 'Notes:'}
          </Typography>
          <Typography>{lineValue(meta?.notes || row?.desc)}</Typography>
        </Box>

        {this.renderSignatures(isRTL)}
      </>
    );
  };

  render() {
    const { company, isRTL, printData } = this.props;
    if (!printData) return <></>;

    const row = printData;
    const employee = printData?.employeeProfile || {};
    const meta = getMeta(row);
    const mode =
      meta?.kind === 'startWork' && meta?.mode
        ? meta.mode
        : row?.isVacation
          ? 'return'
          : 'new';

    return (
      <Box
        className="start-work-print"
        style={{ direction: isRTL ? 'rtl' : 'ltr', padding: 20, fontSize: 14 }}
      >
        <style>
          {`@media print {
            @page {
              size: A4 portrait;
              margin: 8mm;
            }
            .start-work-print {
              width: 100%;
              max-width: 210mm;
              margin: 0 auto;
            }
          }`}
        </style>
        <Grid container spacing={0}>
          {this.renderHeader(company)}
          <Grid item xs={12}>
            {mode === 'return'
              ? this.renderReturnFromVacationForm({
                  row,
                  employee,
                  isRTL,
                  meta,
                })
              : this.renderNewStartForm({ row, employee, isRTL, meta })}
          </Grid>
        </Grid>
      </Box>
    );
  }
}

export default StartWorkPrint;
